<template>
    <div
        data-testid="step-declarationAcceptance"
        class="container step--declaration-acceptance"
    >
        <h1 class="container__standard-header">
            {{ $t(`${TRANSLATION_KEY}.statements`) }}
        </h1>

        <Popup
            :is-open="isOpenInformationForUsTaxPayer"
            cy-data="popup-us-tax-payer"
            @button-clicked="popupHandle('usTaxPayerPopup')"
        >
            <span class="fs-11">
                <strong>{{ $t(`${TRANSLATION_KEY}.fatcaDisclaimer`) }}</strong>
                {{ $t(`${TRANSLATION_KEY}.usTaxPayerInformation`) }}
            </span>
        </Popup>

        <Popup
            :is-open="isOpenInformationForPoliticallyExposedPerson"
            cy-data="popup-pep"
            class="popup--pep"
            @button-clicked="popupHandle('politicallyExposedPersonPopup')"
        >
            <div>
                <strong>{{
                    $t(`${TRANSLATION_KEY}.politicallyExposedPersonDisclaimer`)
                }}</strong>
                <div
                    class="row--person-politically-exposed__row__disclaimer-link"
                >
                    <a
                        :href="
                            $t(
                                `${TRANSLATION_KEY}.politicallyExposedPersonFile`
                            )
                        "
                        rel="noopener"
                        target="_blank"
                    >
                        {{
                            $t(
                                `${TRANSLATION_KEY}.politicallyExposedPersonFileText`
                            )
                        }},
                    </a>
                </div>
                <br />
                <div class="criminal-statement">
                    {{
                        $t(
                            `${TRANSLATION_KEY}.potentialPeoplesCriminalStatement`
                        )
                    }}
                </div>
                <h2>
                    {{
                        $t(
                            `${TRANSLATION_KEY}.politicallyExposedPersonInformationHeader`
                        )
                    }}
                </h2>
                <ul>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesOne`) }}</li>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesTwo`) }}</li>
                    <li>
                        {{ $t(`${TRANSLATION_KEY}.potentialPeoplesThree`) }}
                    </li>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesFour`) }}</li>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesFive`) }}</li>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesSix`) }}</li>
                    <li>
                        {{ $t(`${TRANSLATION_KEY}.potentialPeoplesSeven`) }}
                    </li>
                    <li>
                        {{ $t(`${TRANSLATION_KEY}.potentialPeoplesEight`) }}
                    </li>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesNine`) }}</li>
                    <li>{{ $t(`${TRANSLATION_KEY}.potentialPeoplesTen`) }}</li>
                    <li>
                        {{ $t(`${TRANSLATION_KEY}.potentialPeoplesEleven`) }}
                    </li>
                    <li>
                        {{ $t(`${TRANSLATION_KEY}.potentialPeoplesTwelve`) }}
                    </li>
                </ul>
                <div class="warning">
                    {{ $t(`${TRANSLATION_KEY}.potentialPeoplesWarning`) }}
                </div>
            </div>
        </Popup>

        <Popup
            :is-open="isOpenDataUserDeclarationInfo"
            cy-data="popup-data-user-declaration-open"
            @button-clicked="isOpenDataUserDeclarationInfo = false"
        >
            <span class="fs-11">
                <p>
                    {{
                        $t(
                            `${TRANSLATION_KEY}.dataUserDeclarationTooltipHeaderInfoOne`
                        )
                    }}
                </p>
                <p>
                    {{
                        $t(
                            `${TRANSLATION_KEY}.dataUserDeclarationTooltipHeaderInfoTwo`
                        )
                    }}
                </p>
                <strong>{{ $t(`${TRANSLATION_KEY}.nonProfessional`) }}</strong>
                <p>
                    {{
                        $t(
                            `${TRANSLATION_KEY}.dataUserDeclarationNonProfessionalInfo`
                        )
                    }}
                </p>
                <p>
                    {{
                        $t(
                            `${TRANSLATION_KEY}.dataUserDeclarationNonProfessionalInfoHeader`
                        )
                    }}
                </p>
                <ul>
                    <li>
                        {{
                            $t(
                                `${TRANSLATION_KEY}.dataUserDeclarationNonProfessionalInfoOne`
                            )
                        }}
                    </li>
                    <li>
                        {{
                            $t(
                                `${TRANSLATION_KEY}.dataUserDeclarationNonProfessionalInfoTwo`
                            )
                        }}
                    </li>
                    <li>
                        {{
                            $t(
                                `${TRANSLATION_KEY}.dataUserDeclarationNonProfessionalInfoThree`
                            )
                        }}
                    </li>
                </ul>
                <p />
                <strong>{{ $t(`${TRANSLATION_KEY}.professional`) }}</strong>
                <ul>
                    <li>
                        {{
                            $t(
                                `${TRANSLATION_KEY}.dataUserDeclarationProfessionalInfo`
                            )
                        }}
                    </li>
                </ul>
            </span>
        </Popup>

        <form
            autocomplete="off"
            :style="{
                display:
                    isOpenInformationForUsTaxPayer ||
                    isOpenInformationForPoliticallyExposedPerson ||
                    isOpenDataUserDeclarationInfo
                        ? 'none'
                        : 'block',
            }"
            @submit.prevent="null"
        >
            <div class="row--us-taxpayer">
                <div class="row--us-taxpayer__row">
                    <div class="row--us-taxpayer__question">
                        {{ $t(`${TRANSLATION_KEY}.isUsTaxPayer`) }}
                    </div>

                    <div class="row--person-politically-exposed__moreInfo">
                        <span
                            ref="usTaxPayerMoreInormation"
                            class="row--us-taxpayer__more-info"
                            data-info="usTaxPayerPopup"
                            cy-data="popup-us-tax-payer-open"
                            @click="popupHandle('usTaxPayerPopup')"
                        >
                            ?
                        </span>
                    </div>
                </div>
                <div class="row--us-taxpayer__choice">
                    <Radio
                        :label="$t(`${TRANSLATION_KEY}.no`)"
                        name="usaTaxPayer"
                        :value="false"
                        :checked="formData.usaTaxPayer === false"
                        :handles-error="false"
                        cy-data="us-tax-payer-no"
                        @update-value="
                            ($event) => (formData.usaTaxPayer = $event)
                        "
                    />
                    <Radio
                        :label="$t(`${TRANSLATION_KEY}.yes`)"
                        name="usaTaxPayer"
                        :value="true"
                        :checked="formData.usaTaxPayer === true"
                        cy-data="us-tax-payer-yes"
                        @update-value="formData.usaTaxPayer = $event"
                    />
                </div>
                <div v-if="formData.usaTaxPayer" class="row--us-taxpayer__tin">
                    <Field
                        v-model="formData.usaTin"
                        type="text"
                        name="usaTin"
                        cy-data="us-tax-payer-tin"
                        :placeholder="
                            $t(`${TRANSLATION_KEY}.usaTinPlaceholder`)
                        "
                        :is-numeric="true"
                    >
                        {{ $t(`${TRANSLATION_KEY}.usaTinLabel`) }}
                    </Field>
                </div>
            </div>
            <div class="row--person-politically-exposed">
                <div class="row--person-politically-exposed__row">
                    <div class="row--person-politically-exposed__question">
                        {{ $t(`${TRANSLATION_KEY}.politicallyExposedPerson`) }}
                    </div>
                    <div class="row--person-politically-exposed__moreInfo">
                        <span
                            ref="politicallyExposedPersonMoreInormation"
                            class="row--person-politically-exposed__more-info"
                            data-info="politicallyExposedPersonPopup"
                            cy-data="popup-pep-open"
                            @click="
                                popupHandle('politicallyExposedPersonPopup')
                            "
                        >
                            ?
                        </span>
                    </div>
                </div>
                <div class="row--person-politically-exposed__choice">
                    <Radio
                        :label="$t(`${TRANSLATION_KEY}.no`)"
                        name="pepDeclaration"
                        :value="false"
                        :checked="formData.pepDeclaration === false"
                        :handles-error="false"
                        cy-data="pep-no"
                        @update-value="formData.pepDeclaration = $event"
                    />
                    <Radio
                        :label="$t(`${TRANSLATION_KEY}.yes`)"
                        name="pepDeclaration"
                        :value="true"
                        :checked="formData.pepDeclaration === true"
                        cy-data="pep-yes"
                        @update-value="formData.pepDeclaration = $event"
                    />
                </div>
            </div>
            <div class="row--data-user">
                <div class="row--data-user__row">
                    <div class="row--data-user__question">
                        {{ $t(`${TRANSLATION_KEY}.dataUserDeclaration`) }}
                    </div>
                    <div class="row--data-user__moreInfo">
                        <span
                            ref="dataUserDeclarationMoreInfo"
                            class="row--data-user__more-info"
                            data-info="dataUserDeclarationPopup"
                            cy-data="popup-data-user-declaration-open"
                            @click="isOpenDataUserDeclarationInfo = true"
                        >
                            ?
                        </span>
                    </div>
                </div>
                <div class="row--data-user__choice">
                    <Radio
                        :label="$t(`${TRANSLATION_KEY}.nonProfessional`)"
                        name="dataUserDeclaration"
                        :value="DataUserDeclaration.NON_PROFESSIONAL"
                        :checked="
                            formData.dataUserDeclaration ===
                            DataUserDeclaration.NON_PROFESSIONAL
                        "
                        :handles-error="false"
                        cy-data="data-user-declarationnon-professional"
                        @update-value="formData.dataUserDeclaration = $event"
                    />
                    <Radio
                        :label="$t(`${TRANSLATION_KEY}.professional`)"
                        name="dataUserDeclaration"
                        :value="DataUserDeclaration.PROFESSIONAL"
                        :checked="
                            formData.dataUserDeclaration ===
                            DataUserDeclaration.PROFESSIONAL
                        "
                        cy-data="data-user-declaration-professional"
                        @update-value="formData.dataUserDeclaration = $event"
                    />
                </div>
            </div>
            <div v-if="isStocksAccountType">
                <div class="row--data-user">
                    <div class="row--data-user__row">
                        <div class="row--data-user__question">
                            {{ $t(`${TRANSLATION_KEY}.esgQuestionFirst`) }}
                        </div>
                    </div>
                    <div class="row--data-user__choice">
                        <Radio
                            :label="$t(`${TRANSLATION_KEY}.yes`)"
                            name="esgQuestionFirst"
                            :value="EsgQuestionFirst.INVESTMENTS_YES"
                            :checked="
                                formData.esgQuestionFirst ===
                                EsgQuestionFirst.INVESTMENTS_YES
                            "
                            :handles-error="false"
                            cy-data="data-user-esg-question-first-yes"
                            @update-value="handleEsgQuestionFirstUpdate"
                        />
                        <Radio
                            :label="$t(`${TRANSLATION_KEY}.no`)"
                            name="esgQuestionFirst"
                            :value="EsgQuestionFirst.INVESTMENTS_NO"
                            :checked="
                                formData.esgQuestionFirst ===
                                EsgQuestionFirst.INVESTMENTS_NO
                            "
                            cy-data="data-user-esg-question-first-no"
                            @update-value="handleEsgQuestionFirstUpdate"
                        />
                    </div>
                </div>
                <div
                    v-if="
                        formData.esgQuestionFirst ===
                        EsgQuestionFirst.INVESTMENTS_YES
                    "
                    class="row--data-user"
                >
                    <div class="row--data-user__row">
                        <div class="row--data-user__question">
                            {{ $t(`${TRANSLATION_KEY}.esgQuestionSecond`) }}
                        </div>
                    </div>
                    <div class="row--data-user__choice">
                        <Radio
                            :label="$t(`${TRANSLATION_KEY}.yes`)"
                            name="esgQuestionSecond"
                            :value="EsgQuestionSecond.LIMITED_YES"
                            :checked="
                                formData.esgQuestionSecond ===
                                EsgQuestionSecond.LIMITED_YES
                            "
                            :handles-error="false"
                            cy-data="data-user-esg-question-second-yes"
                            @update-value="formData.esgQuestionSecond = $event"
                        />
                        <Radio
                            :label="$t(`${TRANSLATION_KEY}.no`)"
                            name="esgQuestionSecond"
                            :value="EsgQuestionSecond.LIMITED_NO"
                            :checked="
                                formData.esgQuestionSecond ===
                                EsgQuestionSecond.LIMITED_NO
                            "
                            cy-data="data-user-esg-question-second-no"
                            @update-value="formData.esgQuestionSecond = $event"
                        />
                    </div>
                </div>
                <div class="row__data-information">
                    <p>
                        {{ $t(`${TRANSLATION_KEY}.esgDisclaimerHeader`) }}
                    </p>
                    <p>
                        {{ $t(`${TRANSLATION_KEY}.esgDisclaimerItemOne`) }}
                    </p>
                    <p>
                        {{ $t(`${TRANSLATION_KEY}.esgDisclaimerItemTwo`) }}
                    </p>
                    <p>
                        {{ $t(`${TRANSLATION_KEY}.esgDisclaimerFooter`) }}
                    </p>
                </div>
                <div
                    v-if="
                        isStocksAccountType &&
                        formData.esgQuestionFirst ===
                            EsgQuestionFirst.INVESTMENTS_YES &&
                        formData.esgQuestionSecond ===
                            EsgQuestionSecond.LIMITED_NO
                    "
                >
                    <p class="container__subheader t-bold t-center">
                        {{ $t(`${TRANSLATION_KEY}.esgQuestionFailureInfo`) }}
                    </p>
                    <div class="row--data-user">
                        <CheckBox
                            v-model="formData.esgQuestionFailure"
                            name="esgQuestionFailure"
                            :is-validated="false"
                        >
                            {{ $t(`${TRANSLATION_KEY}.esgQuestionFailure`) }}
                        </CheckBox>
                    </div>
                    <div
                        class="row--acceptance-of-documents__annotation t-bold"
                    />
                    <SubmitButton
                        :action="Action.DECLARATION_ACCEPTANCE"
                        :button-value="$t(`${TRANSLATION_KEY}.esgSubmit`)"
                        :data="formData"
                        class="button button--submit"
                        :disabled="
                            !formEsgFailedValid || !formData.esgQuestionFailure
                        "
                        @click="handleNextStep"
                    />
                </div>
            </div>
            <div :class="[formEsgFailedValid && 'blur-div-wrapper']">
                <p class="container__subheader t-bold t-center">
                    {{ $t(`${TRANSLATION_KEY}.agreementsHeader`) }}
                </p>
                <div
                    class="row--acceptance-of-documents declaration-step-checkbox-wrapper"
                >
                    <div v-if="isStocksAccountType">
                        <div class="row--documents" cy-data="document-list">
                            <div class="download-all-link">
                                <img
                                    class="download-icon"
                                    src="@/assets/download-icon.svg"
                                    alt=""
                                />
                                <CheckBox
                                    v-model="formData.downloadAll"
                                    name="downloadAll"
                                    :value="false"
                                    :is-validated="false"
                                >
                                    {{
                                        $t(
                                            `${TRANSLATION_KEY}.downloadAllStock`
                                        )
                                    }}
                                </CheckBox>
                            </div>
                        </div>
                    </div>
                    <div v-if="!isStocksAccountType">
                        <div
                            class="row--acceptance-of-documents declaration-step-checkbox-wrapper"
                        >
                            <div class="row--documents" cy-data="document-list">
                                <div>
                                    <div class="download-all-link">
                                        <img
                                            class="download-icon"
                                            src="@/assets/download-icon.svg"
                                            alt=""
                                        />
                                        <CheckBox
                                            v-model="formData.downloadAll"
                                            name="downloadAll"
                                            :value="false"
                                            :is-validated="false"
                                        >
                                            {{
                                                $t(
                                                    `${TRANSLATION_KEY}.downloadAll`
                                                )
                                            }}
                                        </CheckBox>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <CheckBox
                        v-if="isStocksAccountType"
                        v-model="formData.statementReceiveStocksKid"
                        name="statementReceiveStocksKid"
                        :value="false"
                        cy-data="received-kid"
                        text="$t(`${TRANSLATION_KEY}.statementReceiveStocksKid`)"
                    >
                        <span
                            ref="statementReceiveStocksKid"
                            class="row--acceptance-of-documents__label"
                        >
                            {{
                                $t(
                                    `${TRANSLATION_KEY}.statementReceiveStocksKid`
                                )
                            }}
                        </span>
                    </CheckBox>
                    <CheckBox
                        v-model="formData.documentsAcceptance"
                        name="documentsAcceptance"
                        :value="false"
                        cy-data="accept-documents"
                    >
                        <span class="row--acceptance-of-documents__label">
                            {{ $t(`${TRANSLATION_KEY}.acceptanceOfDocuments`) }}
                        </span>
                    </CheckBox>
                    <CheckBox
                        v-if="consents.includes('RECEIVED_KID')"
                        v-model="formData.receivedKid"
                        name="receivedKid"
                        :value="false"
                        cy-data="received-kid"
                        text="$t(`${TRANSLATION_KEY}.receivedKid`)"
                    >
                        <span
                            ref="receivedKidRef"
                            class="row--acceptance-of-documents__label"
                        >
                            {{ $t(`${TRANSLATION_KEY}.receivedKid`) }}
                        </span>
                    </CheckBox>
                    <CheckBox
                        v-if="
                            consents.includes('UNDERSTOOD_FEATURES_AND_RISKS')
                        "
                        v-model="formData.understoodFeaturesAndRisks"
                        name="understoodFeaturesAndRisks"
                        :value="false"
                        cy-data="understood-features"
                    >
                        <span
                            ref="understoodFeaturesRisksRef"
                            class="row--acceptance-of-documents__label"
                        >
                            {{
                                $t(`${TRANSLATION_KEY}.understoodFeaturesRisks`)
                            }}
                        </span>
                    </CheckBox>
                    <CheckBox
                        v-if="
                            consents.includes('AWARE_OF_LOSS_AND_CREDIT_RISKS')
                        "
                        v-model="formData.awareOfLossAndCreditRisks"
                        name="awareOfLossAndCreditRisks"
                        :value="false"
                        cy-data="aware-of-loss"
                    >
                        <span
                            ref="awareOfLossRisksRef"
                            class="row--acceptance-of-documents__label"
                        >
                            {{ $t(`${TRANSLATION_KEY}.awareOfLossRisks`) }}
                        </span>
                    </CheckBox>
                    <CheckBox
                        v-if="otherThanPLAndEN()"
                        v-model="formData.confirmEnglishKnowledgeAgreement"
                        name="confirmEnglishKnowledgeAgreement"
                        :value="false"
                        cy-data="confirm-english-knowledge-agreement"
                        class="m-bottom"
                    >
                        <span class="row--acceptance-of-documents__label">
                            {{
                                $t(
                                    `${TRANSLATION_KEY}.acceptanceOfEnglishKnowledgeAgreementLabel`
                                )
                            }}
                        </span>
                    </CheckBox>
                </div>

                <div class="row">
                    <button
                        type="button"
                        :class="[
                            'row--show-documents',
                            showDocuments && 'active',
                        ]"
                        :aria-expanded="showDocuments"
                        aria-controls="declaration-documents-list"
                        @click="documentsShow"
                    >
                        <img
                            src="@/assets/plus.svg"
                            alt=""
                            aria-hidden="true"
                        />
                        <span>{{
                            $t(`${TRANSLATION_KEY}.showAllDocuments`)
                        }}</span>
                    </button>
                </div>
                <div
                    v-if="isStocksAccountType"
                    id="declaration-documents-list"
                    :class="[
                        'document-list',
                        showDocuments && 'document-list-show',
                    ]"
                >
                    <a
                        :href="
                            $t(`${TRANSLATION_KEY}.documentInformationCardFile`)
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.documentInformationCard`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.termsOfServiceFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.termsOfService`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.frameworkAgreementFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.frameworkAgreement`) }}
                    </a>
                    <a
                        :href="
                            $t(
                                `${TRANSLATION_KEY}.stocksSupplementFrameworkAgreementLink`
                            )
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{
                            $t(
                                `${TRANSLATION_KEY}.stocksSupplementFrameworkAgreement`
                            )
                        }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.financialInstrumentFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.financialInstrument`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.costsTablesStocksFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.costsTablesStocks`) }}
                    </a>
                    <a
                        :href="
                            $t(
                                `${TRANSLATION_KEY}.financialInstumentsSpecificationFileStocks`
                            )
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{
                            $t(
                                `${TRANSLATION_KEY}.financialInstumentsSpecificationStocks`
                            )
                        }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.orderExecutionOrderFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.orderExecutionOrder`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.exAnteFileStocks`)"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.exAnteStocks`) }}
                    </a>
                </div>
                <div
                    v-else
                    id="declaration-documents-list"
                    :class="[
                        'document-list',
                        showDocuments && 'document-list-show',
                    ]"
                >
                    <a
                        :href="
                            $t(`${TRANSLATION_KEY}.documentInformationCardFile`)
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.documentInformationCard`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.termsOfServiceFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.termsOfService`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.frameworkAgreementFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.frameworkAgreement`) }}
                    </a>
                    <a
                        :href="
                            $t(
                                `${TRANSLATION_KEY}.cfdsSupplementFrameworkAgreementLink`
                            )
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{
                            $t(
                                `${TRANSLATION_KEY}.cfdsSupplementFrameworkAgreement`
                            )
                        }}
                    </a>
                    <a
                        :href="
                            $t(`${TRANSLATION_KEY}.keyProductInformationFile`)
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.keyProductInformation`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.financialInstrumentFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.financialInstrument`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.costsTablesFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.costsTables`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.rollTableInstumentsFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.rollTableInstuments`) }}
                    </a>
                    <a
                        :href="
                            $t(
                                `${TRANSLATION_KEY}.financialInstumentsSpecificationFile`
                            )
                        "
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{
                            $t(
                                `${TRANSLATION_KEY}.financialInstumentsSpecification`
                            )
                        }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.orderExecutionOrderFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.orderExecutionOrder`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.swapTableFile`)"
                        :tabindex="showDocuments ? 0 : -1"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.swapTable`) }}
                    </a>
                    <a
                        :href="$t(`${TRANSLATION_KEY}.exAnteFileCfds`)"
                        rel="noopener"
                        target="_blank"
                    >
                        {{ $t(`${TRANSLATION_KEY}.exAnteCfds`) }}
                    </a>
                </div>
                <div class="row--acceptance-of-documents__annotation t-bold">
                    {{
                        $t(
                            `${TRANSLATION_KEY}.informationAboutSendingDocuments`
                        )
                    }}
                </div>
            </div>

            <SubmitButton
                :action="Action.DECLARATION_ACCEPTANCE"
                :button-value="$t(`${TRANSLATION_KEY}.next`)"
                :data="formData"
                data-testid="btn-declarationAcceptance-nextStep"
                class="button button--submit"
                :disabled="formIsValid || formEsgFailedValid"
                @click="handleNextStep"
            />
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    reactive,
    computed,
    onMounted,
    watch,
} from 'vue';
import { SET_PROGRESS } from '@/constants/mutation-types';
import { DataHelper } from '@/helpers/DataHelper';
import { Locale } from '@/constants/locale';
import Select from '@/components/basic/fields/Select.vue';
import Radio from '@/components/basic/fields/Radio.vue';
import CheckBox from '@/components/basic/fields/CheckBox.vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import Popup from '@/components/basic/Popup.vue';
import Field from '@/components/basic/fields/Field.vue';
import i18n from '@/i18n';
import {
    DataUserDeclaration,
    EsgQuestionFirst,
    EsgQuestionSecond,
} from '@/constants/form-values';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { useStore } from 'vuex';
import { Action } from '@/constants/ajax';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';

export default defineComponent({
    name: 'DeclarationAcceptance',
    components: {
        CheckBox,
        Radio,
        Select,
        SubmitButton,
        Popup,
        Field,
    },
    setup() {
        const store = useStore();
        const TRANSLATION_KEY = 'steps.DeclarationAcceptance';
        const formData = reactive({
            usaTaxPayer: null,
            pepDeclaration: null,
            investmentGoal: '',
            incomeSource: '',
            downloadAll: false,
            documentsAcceptance: false,
            usaTin: '',
            confirmElectronicAgreement: false,
            dataUserDeclaration: '',
            esgQuestionFirst: '',
            esgQuestionSecond: '',
            esgQuestionFailure: true,
            receivedKid: false,
            receivedKidLabel: '',
            understoodFeaturesAndRisks: false,
            understoodFeaturesAndRisksLabel: '',
            awareOfLossAndCreditRisks: false,
            awareOfLossAndCreditRisksLabel: '',
            statementReceiveStocksKid: false,
            confirmEnglishKnowledgeAgreement: undefined as boolean | undefined,
        });

        const purposeContract = ref<unknown[]>([]);
        const sourceEarnings = ref<unknown[]>([]);
        const isOpenInformationForUsTaxPayer = ref(false);
        const isOpenInformationForPoliticallyExposedPerson = ref(false);
        const isOpenDataUserDeclarationInfo = ref(false);
        const isAllCheckboxChecked = ref(false);
        const hasEsgValid = ref(false);
        // const formIsValid = ref(false);
        const showDocuments = ref(false);
        const consents = computed(() => store.state.consents || []);

        const loadJSONData = async () => {
            const data = await DataHelper.loadJSON(i18n.global.locale.value);
            Object.entries(data).forEach(([key, value]) => {
                if (key === 'purposeContract') {
                    purposeContract.value = Array.isArray(value) ? value : [];
                } else if (key === 'sourceEarnings') {
                    sourceEarnings.value = Array.isArray(value) ? value : [];
                }
            });
        };

        const popupHandle = (dataInfo: string) => {
            if (dataInfo.includes('usTaxPayer')) {
                isOpenInformationForUsTaxPayer.value =
                    !isOpenInformationForUsTaxPayer.value;
            } else {
                isOpenInformationForPoliticallyExposedPerson.value =
                    !isOpenInformationForPoliticallyExposedPerson.value;
            }
        };

        const isValid = () => {
            const checkboxWrapper = document.querySelector(
                '.declaration-step-checkbox-wrapper'
            );

            if (checkboxWrapper) {
                const checkboxes = checkboxWrapper.querySelectorAll(
                    "input[type='checkbox']"
                );
                const checkedCheckboxes = checkboxWrapper.querySelectorAll(
                    "input[type='checkbox']:checked"
                );
                isAllCheckboxChecked.value =
                    checkboxes.length === checkedCheckboxes.length;
            }
        };

        const handleNextStep = () => {
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.statements.STATEMENTS_CONTINUE
            );
        };

        const handleEsgQuestionFirstUpdate = (value: string) => {
            formData.esgQuestionFirst = value;

            if (value === EsgQuestionFirst.INVESTMENTS_NO) {
                formData.esgQuestionSecond = '';
            }
        };

        const otherThanPLAndEN = () => {
            return !(
                i18n.global.locale.value.toUpperCase() === 'PL' ||
                i18n.global.locale.value.toUpperCase() === 'EN'
            );
        };

        const documentsShow = () => {
            showDocuments.value = !showDocuments.value;
        };

        const getDownloadAllUrl = () => {
            const baseUrl = isStocksAccountType.value
                ? i18n.global.t(`${TRANSLATION_KEY}.downloadAllStockLink`)
                : i18n.global.t(`${TRANSLATION_KEY}.downloadAllLinkCfds`);

            return `${baseUrl}?date=${dateParam.value}`;
        };

        const isStocksAccountType = computed(() => {
            const accountType =
                store.state.formData.accountType ??
                store.state.response?.dataContent?.accountType ??
                null;
            return accountType == 2;
        });

        const formIsValid = computed(() => {
            // @TODO refactor code
            const hasTin =
                formData.usaTaxPayer === false ? true : !!formData.usaTin; // safe conversion to boolean

            if (isStocksAccountType.value && formData.esgQuestionFirst) {
                hasEsgValid.value =
                    formData.esgQuestionFirst ===
                    EsgQuestionFirst.INVESTMENTS_YES
                        ? formData.esgQuestionSecond
                            ? true
                            : false
                        : true;
            }

            return !(
                (isStocksAccountType.value &&
                    hasEsgValid.value &&
                    isAllCheckboxChecked.value &&
                    hasTin &&
                    formData.pepDeclaration !== null &&
                    formData.dataUserDeclaration) ||
                (!isStocksAccountType.value &&
                    isAllCheckboxChecked.value &&
                    hasTin &&
                    formData.pepDeclaration !== null &&
                    formData.dataUserDeclaration)
            );
        });

        const dateParam = computed(() => {
            const now = new Date();
            const Y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const d = String(now.getDate()).padStart(2, '0');
            const H = String(now.getHours()).padStart(2, '0');
            return `${Y}${m}${d}${H}`;
        });

        const formEsgFailedValid = computed(() => {
            const hasTin =
                formData.usaTaxPayer === false ? true : !!formData.usaTin; // safe conversion to boolean

            return Boolean(
                isStocksAccountType.value &&
                hasTin &&
                formData.pepDeclaration !== null &&
                formData.dataUserDeclaration &&
                formData.esgQuestionFirst ===
                    EsgQuestionFirst.INVESTMENTS_YES &&
                formData.esgQuestionSecond === EsgQuestionSecond.LIMITED_NO
            );
        });

        const fetchAccountType = async (): Promise<void> => {
            store.state.isLocked = 'true';
            const ajaxService = store.state.ajaxService;
            try {
                const response = await ajaxService.post(
                    ajaxService.RESOURCES[Action.GET_ACCOUNT_TYPE],
                    {
                        token: ajaxService.getToken(),
                    }
                );

                if (response.status === 'error') {
                    store.state.globalError = response.errors;
                    return;
                }

                const accountType =
                    response.accountType ?? response.data?.accountType ?? null;

                if (accountType) {
                    store.state.formData.accountType = accountType;
                    store.state.isLocked = false;
                }
            } catch (error: any) {
                store.state.globalError = error;
            } finally {
                store.state.isLocked = false;
            }
        };

        watch(
            () => formData.usaTaxPayer,
            () => {
                formData.usaTin = '';
            }
        );

        watch(
            () => formData.downloadAll,
            (newValue) => {
                if (newValue) {
                    window.open(getDownloadAllUrl(), '_parent');
                }
            }
        );

        onMounted(() => {
            if (
                !store.state.formData.allowedAccountTypes ||
                Object.keys(store.state.formData.accountType).length === 0
            ) {
                fetchAccountType();
            }
            store.commit(
                SET_PROGRESS,
                PROGRESS_BAR_TIMES.DECLARATION_ACCEPTANCE
            );
            loadJSONData();
            isValid();
            setInterval(isValid, 1000);
        });

        return {
            i18n,
            Locale,
            DataUserDeclaration,
            TRANSLATION_KEY,
            formData,
            purposeContract,
            sourceEarnings,
            isOpenInformationForUsTaxPayer,
            isOpenInformationForPoliticallyExposedPerson,
            isOpenDataUserDeclarationInfo,
            isAllCheckboxChecked,
            formIsValid,
            showDocuments,
            consents,
            popupHandle,
            isValid,
            documentsShow,
            otherThanPLAndEN,
            handleNextStep,
            handleEsgQuestionFirstUpdate,
            isStocksAccountType,
            EsgQuestionFirst,
            EsgQuestionSecond,
            formEsgFailedValid,
            dateParam,
            getDownloadAllUrl,
            Action,
        };
    },
});
</script>

<style lang="scss" scoped>
.container__subheader {
    @media (min-width: 500px) {
        width: 400px;
        max-width: 100%;
    }
    margin: 20px auto;
    font-size: 12px;
    text-align: left;
}
.row--us-taxpayer,
.row--person-politically-exposed {
    .input-wrapper--radio {
        width: 150px;
    }
}
.blur-div-wrapper {
    opacity: 0.4;
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
    transition: opacity 0.3s ease;
}
</style>
